import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, LogOut, Shield, CreditCard, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface UserMenuProps {
  variant?: "compact" | "full";
  showAvatar?: boolean;
  className?: string;
}

const UserMenu: React.FC<UserMenuProps> = ({
  variant = "full",
  showAvatar = true,
  className,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const isAdmin = !!user?.roles?.includes("Admin");
  const isGuardianOrStudent = !!user?.roles?.some(
    (role) => role === "Apoderado" || role === "Estudiante"
  );

  const getInitials = (): string => {
    if (!user) return "U";

    const nombre = user.nombre || "";
    const apellido = user.apellido || "";

    const nombres = nombre.split(" ");
    const apellidos = apellido.split(" ");

    const primeraLetraNombre = nombres[0]?.charAt(0) || "";
    const primeraLetraApellido = apellidos[0]?.charAt(0) || "";

    return (primeraLetraNombre + primeraLetraApellido).toUpperCase();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleProfileClick = () => {
    navigate("/profile");
    setIsOpen(false);
  };

  const handlePaymentsClick = () => {
    navigate("/profile/payments");
    setIsOpen(false);
  };

  const handleAdminDashboardClick = () => {
    navigate("/admin");
    setIsOpen(false);
  };

  const handleUserDashboardClick = () => {
    navigate("/dashboard");
    setIsOpen(false);
  };

  const getRoleDisplay = () => {
    if (!user?.roles) return "";

    const roleMap: Record<string, string> = {
      Admin: "Administrador",
      Apoderado: "Apoderado",
      Cajero: "Cajero",
      Estudiante: "Estudiante",
      Delivery: "Personal de Entrega",
    };

    return user.roles.map((role) => roleMap[role] || role).join(", ");
  };

  if (!user) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate("/login")}
        className={className}
      >
        <User className="h-4 w-4 mr-2" />
        <span className="hidden sm:inline">Iniciar Sesión</span>
        <span className="sm:hidden">Login</span>
      </Button>
    );
  }

  if (variant === "compact") {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={className}>
            {showAvatar && (
              <Avatar className="h-6 w-6 mr-2">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
            )}
            <span className="hidden sm:inline">{user.nombre}</span>
            <span className="sm:hidden">{user.nombre?.charAt(0)}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium">
                {user.nombre} {user.apellido}
              </p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
              <p className="text-xs text-muted-foreground">
                {getRoleDisplay()}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {isAdmin && (
            <>
              <DropdownMenuItem onClick={handleAdminDashboardClick}>
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {!isAdmin && isGuardianOrStudent && (
            <>
              <DropdownMenuItem onClick={handleUserDashboardClick}>
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={handleProfileClick}>
            <User className="h-4 w-4 mr-2" />
            Mi Perfil
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handlePaymentsClick}>
            <CreditCard className="h-4 w-4 mr-2" />
            Mis Pedidos
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-destructive">
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <div className="text-right hidden sm:block">
        <p className="text-sm font-medium text-card-foreground">
          {user.nombre} {user.apellido}
        </p>
        <p className="text-xs text-muted-foreground">{getRoleDisplay()}</p>
      </div>

      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            <span className="hidden sm:inline">Mi Perfil</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium">
                {user.nombre} {user.apellido}
              </p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  {getRoleDisplay()}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {isAdmin && (
            <>
              <DropdownMenuItem onClick={handleAdminDashboardClick}>
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {!isAdmin && isGuardianOrStudent && (
            <>
              <DropdownMenuItem onClick={handleUserDashboardClick}>
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={handleProfileClick}>
            <User className="h-4 w-4 mr-2" />
            Mi Perfil
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handlePaymentsClick}>
            <CreditCard className="h-4 w-4 mr-2" />
            Mis Pedidos
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-destructive">
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default UserMenu;
